import React from "react";

import {
  Button,
  Col,
  Row,
  Spin,
  notification,
  Card,
  Space,
  Typography,
  DatePicker,
  Alert,
} from "antd";
import {
  LineChartOutlined,
  TableOutlined,
  BarChartOutlined,
  DownloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

import { API_URL } from "../../shared/utils/Util";
import { getFetchT } from "../../shared/utils/TypedUtil";
import { Counts, RankStats, TimedCounts, TimedRankStats, TimedStats } from "../../types/api";
import { TimedCountsPlot } from "./TimedCountsPlot";
import { TimedCountsTable } from "./TimedCountsTable";
import { TimedRankStatsTable } from "./TimedRankStatsTable";
import { exportToCsv } from "./StatsUtil";
import { useAntdTheme } from "../../shared/hooks";

type StatsViewProps = {
  restaurantId: string;
};

// Divide all entries in TimedCounts by a number.
function divideTimedCounts(tc: TimedCounts, n: number): TimedCounts {
  let result: TimedCounts = {};
  Object.entries(tc).forEach(([ts, counts]) => {
    result[ts] = {};
    Object.entries(counts).forEach(([key, v]) => {
      result[ts][key] = v / n;
    });
  });
  return result;
}

// Divide all entries in TimedRankStats (except count) by a number.
function divideTimedRankStats(tr: TimedRankStats, n: number): TimedRankStats {
  let result: TimedRankStats = {};
  Object.entries(tr).forEach(([ts, stats]: [string, RankStats]) => {
    result[ts] = { ...stats };
    for (let key of Object.keys(stats)) {
      if (key !== "count") {
        // TypeScript does not allow generic string to be used as field accessor.
        // @ts-ignore
        result[ts][key] = result[ts][key] / n;
      }
    }
  });
  return result;
}

export const StatsView = (props: StatsViewProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const week_ago = dayjs().startOf("day").subtract(7, "days");
  const tomorrow = dayjs().startOf("day").add(1, "days");

  const [stats, setStats] = React.useState<TimedStats | null>(null);
  const [dateRange, setDateRange] = React.useState<[Dayjs, Dayjs]>([week_ago, tomorrow]);
  const [display_plot, setDisplayPlot] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<boolean>(false);

  const [start_time, end_time] = dateRange;

  const getStats = () => {
    setLoading(true);
    setError(false);
    // Send dates in local time with timezone offset
    const start_local = start_time.format();
    const end_local = end_time.format();
    getFetchT<TimedStats>(
      `${API_URL}/v1/r/${props.restaurantId}/stats/${start_local}/${end_local}/daily`,
      (result: TimedStats) => {
        setStats(result);
        setLoading(false);
        setError(false);
      },
      (err) => {
        setLoading(false);
        setError(true);
        notification.error({
          message: t("error"),
          description: t("error_occurred"),
        });
      }
    );
  };
  React.useEffect(getStats, [props.restaurantId]);

  if (loading) {
    // Still loading
    return (
      <div style={{ textAlign: "center", padding: token.paddingXL }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Date Range and Controls */}
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
          background: token.colorBgContainer,
        }}
        styles={{ body: { padding: token.paddingXL } }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={14}>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="YYYY/MM/DD"
                disabledDate={(current) => {
                  // Disable dates more than 14 days apart from the selected start date
                  if (!current) return false;

                  if (dateRange && dateRange[0]) {
                    const maxDate = dateRange[0].add(14, "days");
                    if (current.isAfter(maxDate, "day")) {
                      return true;
                    }
                  }
                  return false;
                }}
                style={{
                  width: "100%",
                  borderRadius: token.borderRadiusLG,
                }}
                placeholder={[t("start_date"), t("end_date")]}
              />
            </Col>
            <Col xs={24} md={10}>
              <Row gutter={[12, 12]}>
                <Col xs={12}>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={getStats}
                    loading={loading}
                    block
                    style={{
                      borderRadius: token.borderRadiusLG,
                      height: token.controlHeight,
                      fontWeight: 600,
                      boxShadow: token.boxShadowTertiary,
                    }}
                  >
                    {t("search")}
                  </Button>
                </Col>
                <Col xs={12}>
                  <Button
                    icon={display_plot ? <TableOutlined /> : <LineChartOutlined />}
                    onClick={() => setDisplayPlot(!display_plot)}
                    block
                    style={{
                      borderRadius: token.borderRadiusLG,
                      height: token.controlHeight,
                      fontWeight: 600,
                      borderColor: token.colorPrimary,
                      color: token.colorPrimary,
                    }}
                  >
                    {display_plot ? t("show_table") : t("show_plot")}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* Error Alert */}
      {(error || stats === null) && (
        <Alert
          message={t("error_loading_statistics")}
          description={t("please_adjust_date_range_and_retry")}
          type="error"
          showIcon
          style={{
            borderRadius: token.borderRadiusLG,
            marginBottom: token.marginXL,
          }}
        />
      )}

      {/* Show stats only if no error and stats exist */}
      {!error && stats && (
        <>
          {/* Orders Section */}
          <Card
            title={
              <Space>
                <BarChartOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
                <Text strong style={{ fontSize: token.fontSizeLG }}>
                  Orders
                </Text>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() =>
                  exportToCsv({ filename: "order_stats.csv", data: stats.order_stats })
                }
                style={{
                  borderRadius: token.borderRadiusLG,
                  height: token.controlHeight,
                }}
              >
                Download Table
              </Button>
            }
            style={{
              borderRadius: token.borderRadiusLG,
              boxShadow: token.boxShadowSecondary,
              border: `1px solid ${token.colorBorder}`,
              marginBottom: token.marginXL,
            }}
            styles={{ body: { padding: token.paddingXL } }}
          >
            {display_plot ? (
              <TimedCountsPlot data={stats.order_stats} />
            ) : (
              <TimedCountsTable data={stats.order_stats} />
            )}
          </Card>

          {/* Registered Customers Section */}
          {stats.registered_customers && (
            <Card
              title={
                <Space>
                  <BarChartOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                  <Text strong style={{ fontSize: token.fontSizeLG }}>
                    Registered Customers
                  </Text>
                </Space>
              }
              style={{
                borderRadius: token.borderRadiusLG,
                boxShadow: token.boxShadowSecondary,
                border: `1px solid ${token.colorBorder}`,
                marginBottom: token.marginXL,
              }}
              styles={{ body: { padding: token.paddingXL } }}
            >
              <TimedCountsTable data={stats.registered_customers} />
            </Card>
          )}

          {/* Order Durations Section */}
          {stats.order_durations && (
            <Card
              title={
                <Space>
                  <BarChartOutlined style={{ fontSize: 20, color: token.colorInfo }} />
                  <Text strong style={{ fontSize: token.fontSizeLG }}>
                    Order Durations (minutes)
                  </Text>
                </Space>
              }
              style={{
                borderRadius: token.borderRadiusLG,
                boxShadow: token.boxShadowSecondary,
                border: `1px solid ${token.colorBorder}`,
                marginBottom: token.marginXL,
              }}
              styles={{ body: { padding: token.paddingXL } }}
            >
              <TimedRankStatsTable data={divideTimedRankStats(stats.order_durations, 60)} />
            </Card>
          )}

          {/* Pickup Time Deltas Section */}
          {stats.pickup_time_deltas && (
            <Card
              title={
                <Space>
                  <BarChartOutlined style={{ fontSize: 20, color: token.colorWarning }} />
                  <Text strong style={{ fontSize: token.fontSizeLG }}>
                    Pickup Time Deltas (minutes)
                  </Text>
                </Space>
              }
              style={{
                borderRadius: token.borderRadiusLG,
                boxShadow: token.boxShadowSecondary,
                border: `1px solid ${token.colorBorder}`,
                marginBottom: token.marginXL,
              }}
              styles={{ body: { padding: token.paddingXL } }}
            >
              <TimedRankStatsTable data={divideTimedRankStats(stats.pickup_time_deltas, 60)} />
            </Card>
          )}

          {/* Items Section */}
          <Card
            title={
              <Space>
                <BarChartOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
                <Text strong style={{ fontSize: token.fontSizeLG }}>
                  Items
                </Text>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => exportToCsv({ filename: "item_stats.csv", data: stats.item_stats })}
                style={{
                  borderRadius: token.borderRadiusLG,
                  height: token.controlHeight,
                }}
              >
                Download Table
              </Button>
            }
            style={{
              borderRadius: token.borderRadiusLG,
              boxShadow: token.boxShadowSecondary,
              border: `1px solid ${token.colorBorder}`,
              marginBottom: token.marginXL,
            }}
            styles={{ body: { padding: token.paddingXL } }}
          >
            {/* too many items to plot, only show table */}
            <TimedCountsTable data={stats.item_stats} />
          </Card>
          {/* TODO(kku): show workstation processing time again once we calculate it again.
      <Card>
        <h1>Average Workstation Processing Time (minutes)</h1>
        {
          display_plot
          ? <TimedCountsPlot data={divideTimedCounts(stats.workstation_process_avg, 60)} />
          : <TimedCountsTable data={divideTimedCounts(stats.workstation_process_avg, 60)} />
        }
      </Card>
      */}
        </>
      )}
    </div>
  );
};

// Convert tips to TimedCounts so we can reuse TimedCountsTable.
function tipsToTimedCounts(tips?: Counts): TimedCounts {
  if (!tips) {
    return {};
  }
  let tc: TimedCounts = {};
  Object.entries(tips).forEach(([ts, tip]) => {
    tc[ts] = { "Amount $": tip / 100 };
  });
  return tc;
}
